// ciso-ui/src/components/RiskAnalysis.jsx

import React, { useState, useEffect } from 'react';
import './RiskAnalysis.css';
import config from '../config';
import VulnerabilityModal from './VulnerabilityModal';
import { generateRiskReport } from './PDFReportGenerator';

function RiskAnalysis() {
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [allParsed, setAllParsed] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [riskResults, setRiskResults] = useState(null);
  const [historicalResults, setHistoricalResults] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSeverity, setSelectedSeverity] = useState(null);
  const [selectedSeverityLabel, setSelectedSeverityLabel] = useState('');
  const [exportingPDF, setExportingPDF] = useState(false);
  
  const assessmentId = localStorage.getItem('current_assessment_id');

  // Fetch customer_id on component mount
  useEffect(() => {
    const fetchCustomerId = async () => {
      try {
        const response = await fetch(
          `${config.apiUrl}/api/customers/me`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('cognitoIdToken')}`
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setCustomerId(data.customer_id);
        }
      } catch (error) {
        console.error('Error fetching customer ID:', error);
      }
    };
    
    fetchCustomerId();
  }, []);

  // Poll for parsing status
  useEffect(() => {
    if (!assessmentId || !customerId) return;
    
    const interval = setInterval(async () => {
      try {
        const response = await fetch(
          `${config.apiUrl}/api/risk-analysis/files/${assessmentId}/status?customer_id=${customerId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('cognitoIdToken')}`
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setUploadedFiles(data.files);
          setAllParsed(data.all_complete && data.ready_for_calculation);
          
          // Stop polling when all complete
          if (data.all_complete) {
            clearInterval(interval);
          }
        }
      } catch (error) {
        console.error('Status polling error:', error);
      }
    }, 2000); // Poll every 2 seconds
    
    return () => clearInterval(interval);
  }, [assessmentId, customerId]);

  // Load historical risk calculations
  useEffect(() => {
    if (!assessmentId || !customerId) return;
    
    loadHistoricalResults();
  }, [assessmentId, customerId]);

  const loadHistoricalResults = async () => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/risk-analysis/results/${assessmentId}?customer_id=${customerId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('cognitoIdToken')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setHistoricalResults(data.calculations);
        
        // Set most recent as current results
        if (data.calculations.length > 0) {
          setRiskResults(data.calculations[0]);
        }
      }
    } catch (error) {
      console.error('Error loading historical results:', error);
    }
  };

  const handleFileSelect = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      alert('Please select files to upload');
      return;
    }

    if (!customerId) {
      alert('Customer ID not loaded yet. Please wait a moment and try again.');
      return;
    }

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(
          `${config.apiUrl}/api/risk-analysis/upload?assessment_id=${assessmentId}&customer_id=${customerId}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('cognitoIdToken')}`
            },
            body: formData
          }
        );

        if (!response.ok) {
          alert(`Failed to upload ${file.name}`);
        }
      } catch (error) {
        console.error('Upload error:', error);
        alert(`Error uploading ${file.name}`);
      }
    }

    // Clear file selection
    setFiles([]);
    
    // Refresh status
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const handleCalculateRisk = async () => {
    if (!allParsed) {
      alert('Please wait for all files to finish parsing');
      return;
    }

    if (!customerId) {
      alert('Customer ID not loaded yet. Please wait a moment and try again.');
      return;
    }

    setCalculating(true);

    try {
      const response = await fetch(
        `${config.apiUrl}/api/risk-analysis/calculate?assessment_id=${assessmentId}&customer_id=${customerId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('cognitoIdToken')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setRiskResults(data);
        
        // Reload historical results
        await loadHistoricalResults();
        
        alert('Risk calculation completed!');
      } else {
        const error = await response.json();
        alert(`Risk calculation failed: ${error.detail}`);
      }
    } catch (error) {
      console.error('Calculation error:', error);
      alert('Error calculating risk');
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'critical': '#dc2626',
      'high': '#ea580c',
      'medium': '#f59e0b',
      'low': '#84cc16'
    };
    return colors[severity] || '#6b7280';
  };

  const handleVulnerabilityClick = (severity, severityLabel) => {
    const severityMap = {
      'critical': 5,
      'high': 4,
      'medium': 3,
      'low': 2
    };
    setSelectedSeverity(severityMap[severity]);
    setSelectedSeverityLabel(severity);
    setModalOpen(true);
  };

  const handleExportPDF = async () => {
    if (!riskResults || !customerId) return;
    
    setExportingPDF(true);
    
    try {
      // Fetch top critical/high vulnerabilities for the report
      const response = await fetch(
        `${config.apiUrl}/api/vulnerabilities/top-critical?customer_id=${customerId}&assessment_id=${assessmentId}&limit=10`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('cognitoIdToken')}`
          }
        }
      );
      
      let vulnerabilities = [];
      if (response.ok) {
        const data = await response.json();
        vulnerabilities = data.vulnerabilities;
      }
      
      // Get customer name
      const customerResponse = await fetch(
        `${config.apiUrl}/api/customers/me`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('cognitoIdToken')}`
          }
        }
      );
      
      let customerName = 'Organization';
      if (customerResponse.ok) {
        const customerData = await customerResponse.json();
        customerName = customerData.company_name || customerData.name || 'Organization';
      }
      
      // Generate PDF
      await generateRiskReport(riskResults, customerName, vulnerabilities);
      
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF report');
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <div className="risk-analysis">
      {/* Workflow Banner */}
      <div className={`workflow-banner ${riskResults ? 'has-results' : ''}`}>
        <h2>Quantitative Risk Analysis</h2>
        {!riskResults && (
          <>
            <p>
              Upload your network data to calculate your organization's financial cyber risk exposure 
              using Monte Carlo simulations and topology analysis.
            </p>
            <div className="workflow-steps">
              <div className="workflow-step">
                <div className="workflow-step-number">1</div>
                <div className="workflow-step-title">Upload Data</div>
                <div className="workflow-step-desc">
                  Upload network diagrams, configs, and vulnerability scans
                </div>
              </div>
              <div className="workflow-step">
                <div className="workflow-step-number">2</div>
                <div className="workflow-step-title">Wait for Parsing</div>
                <div className="workflow-step-desc">
                  Files are automatically parsed and analyzed
                </div>
              </div>
              <div className="workflow-step">
                <div className="workflow-step-number">3</div>
                <div className="workflow-step-title">Calculate Risk</div>
                <div className="workflow-step-desc">
                  Click the button to run MCMC risk calculation
                </div>
              </div>
              <div className="workflow-step">
                <div className="workflow-step-number">4</div>
                <div className="workflow-step-title">View Results</div>
                <div className="workflow-step-desc">
                  See your EAL, top risks, and vulnerabilities
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* File Upload Section - FIRST */}
      <div className="upload-section">
        <div className="file-upload">
          <input 
            type="file" 
            multiple 
            onChange={handleFileSelect}
            accept=".png,.jpg,.jpeg,.pdf,.vsd,.vsdx,.txt,.cfg,.conf,.xlsx,.xls,.csv"
          />
          <button 
            onClick={handleUpload}
            disabled={files.length === 0 || !customerId}
          >
            Upload {files.length > 0 ? `${files.length} file(s)` : 'Files'}
          </button>
        </div>

        {/* Uploaded Files Status */}
        {uploadedFiles.length > 0 && (
          <div className="uploaded-files">
            <h3>📄 Uploaded Files ({uploadedFiles.length})</h3>
            <ul>
              {uploadedFiles.map(file => (
                <li key={file.file_id} className={`file-status-${file.status}`}>
                  <span className="filename">{file.filename}</span>
                  <span className="file-type">{file.file_type}</span>
                  <span className="status">
                    {file.status === 'completed' && '✓ Parsed'}
                    {file.status === 'in_progress' && '⏳ Parsing...'}
                    {file.status === 'pending' && '⏸ Queued'}
                    {file.status === 'failed' && '✗ Failed'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Calculate Risk Button - SECOND */}
      <div className="calculate-section">
        <button 
          onClick={handleCalculateRisk}
          disabled={!allParsed || calculating || !customerId}
          className="calculate-button"
        >
          {calculating ? 'Calculating... (60-90 seconds)' : 'Calculate Financial Risk'}
        </button>
        
        {!allParsed && uploadedFiles.length > 0 && (
          <p className="status-message">
            Waiting for files to finish parsing...
          </p>
        )}
      </div>

      {/* Results Section - THIRD (only shows after calculation) */}
      {riskResults && (
        <>
          {/* Current Risk Summary */}
          <div className="risk-summary">
            <div className="risk-card">
              <h3>Expected Annual Loss (EAL)</h3>
              <div className="eal-amount">
                {formatCurrency(riskResults.total_eal)}
              </div>
              <div className="confidence-interval">
                95% CI: [{formatCurrency(riskResults.confidence_intervals.lower)}, 
                {formatCurrency(riskResults.confidence_intervals.upper)}]
              </div>
              <div className="calc-date">
                Calculated: {formatDate(riskResults.created_at)}
              </div>
              <button 
                className="export-pdf-button"
                onClick={handleExportPDF}
                disabled={exportingPDF}
              >
                {exportingPDF ? '📄 Generating PDF...' : '📄 Export PDF Report'}
              </button>
            </div>
          </div>

          {/* Hub Criticality - Top Risk Assets */}
          {riskResults.top_risk_drivers && riskResults.top_risk_drivers.length > 0 && (
            <div className="hub-criticality-section">
              <h3>Top Risk Assets (Hub Criticality)</h3>
              <p className="section-description">These assets contribute the most to your overall risk. Prioritize securing these first.</p>
              
              <div className="hub-criticality-grid">
                {riskResults.top_risk_drivers.map((asset, index) => (
                  <div key={index} className="hub-card">
                    <div className="hub-rank">#{index + 1}</div>
                    <div className="hub-asset-name">{asset.asset}</div>
                    <div className="hub-eal">{formatCurrency(asset.eal)}</div>
                    <div className="hub-score">
                      Criticality Score: {(asset.score * 100).toFixed(1)}%
                    </div>
                    <div className="hub-percentage">
                      {((asset.eal / riskResults.total_eal) * 100).toFixed(1)}% of total risk
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Vulnerability Breakdown */}
          {riskResults.risk_by_category && (
            <div className="vulnerability-breakdown-section">
              <h3>Vulnerability Breakdown</h3>
              <p className="section-description">Count of vulnerabilities by severity level</p>
              
              <div className="vulnerability-grid">
                {Object.entries(riskResults.risk_by_category).map(([severity, count]) => (
                  <div 
                    key={severity} 
                    className="vulnerability-card clickable"
                    onClick={() => handleVulnerabilityClick(severity, severity)}
                  >
                    <div 
                      className="severity-badge" 
                      style={{ backgroundColor: getSeverityColor(severity) }}
                    >
                      {severity.toUpperCase()}
                    </div>
                    <div className="vulnerability-count">{count}</div>
                    <div className="vulnerability-label">
                      {count === 1 ? 'vulnerability' : 'vulnerabilities'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Risk History - LAST */}
      {historicalResults.length > 1 && (
        <div className="risk-history">
          <h3>Risk Calculation History</h3>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Total EAL</th>
                <th>Change</th>
              </tr>
            </thead>
            <tbody>
              {historicalResults.map((calc, index) => {
                const previousCalc = historicalResults[index + 1];
                const change = previousCalc 
                  ? calc.total_eal - previousCalc.total_eal 
                  : 0;
                
                return (
                  <tr key={calc.risk_calc_id}>
                    <td>{formatDate(calc.created_at)}</td>
                    <td>{formatCurrency(calc.total_eal)}</td>
                    <td className={change < 0 ? 'improvement' : 'increase'}>
                      {change !== 0 && (
                        <>
                          {change < 0 ? '↓' : '↑'} 
                          {formatCurrency(Math.abs(change))}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Vulnerability Drill-Down Modal */}
      <VulnerabilityModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        severity={selectedSeverity}
        severityLabel={selectedSeverityLabel}
        customerId={customerId}
        assessmentId={assessmentId}
      />
    </div>
  );
}

export default RiskAnalysis;
